type PositionTradeSide = 'BUY' | 'SELL';

export interface PositionTradeLike {
  team_code: string;
  side: PositionTradeSide;
  trade_type: string;
  trade_id: number;
  quantity: number;
  price: number;
  executed_at?: string | null;
  counterparty_trader_code: string | null;
}

interface BuildPositionTradeColumnsArgs<TTrade extends PositionTradeLike> {
  positionTeamCode: string;
  trades: readonly TTrade[];
  totalTradingPnl?: number | null;
  realizedPnl?: number | null;
  unrealizedPnl?: number | null;
}

export function groupPositionTradesByTeamCode<TTrade extends PositionTradeLike>(
  trades: readonly TTrade[],
) {
  const grouped = new Map<string, TTrade[]>();

  for (const trade of trades) {
    if (trade.trade_type === 'SETTLEMENT') continue;
    const teamCode = trade.team_code.trim().toUpperCase();
    const existing = grouped.get(teamCode);
    if (existing) {
      existing.push(trade);
    } else {
      grouped.set(teamCode, [trade]);
    }
  }

  return grouped;
}

function summarizeTrades<TTrade extends PositionTradeLike>(trades: readonly TTrade[]) {
  const totalQuantity = trades.reduce((sum, trade) => sum + trade.quantity, 0);
  if (totalQuantity <= 0) {
    return {
      totalQuantity: 0,
      averagePrice: null,
    };
  }

  const notional = trades.reduce((sum, trade) => sum + trade.quantity * trade.price, 0);
  return {
    totalQuantity,
    averagePrice: notional / totalQuantity,
  };
}

function roundPnl(value: number) {
  return Math.round(value * 10000) / 10000;
}

function sortTradesAscending<TTrade extends PositionTradeLike>(trades: readonly TTrade[]) {
  return [...trades].sort((left, right) => {
    const leftTime = left.executed_at ? new Date(left.executed_at).getTime() : 0;
    const rightTime = right.executed_at ? new Date(right.executed_at).getTime() : 0;
    if (leftTime !== rightTime) return leftTime - rightTime;
    return left.trade_id - right.trade_id;
  });
}

function calculateMaterializedTradePnl<TTrade extends PositionTradeLike>(trades: readonly TTrade[]) {
  let signedQuantity = 0;
  let averageEntry = 0;
  let realizedPnl = 0;

  for (const trade of sortTradesAscending(trades)) {
    const tradeQuantity = trade.quantity;
    if (tradeQuantity <= 0) continue;

    const signedDelta = trade.side === 'BUY' ? tradeQuantity : -tradeQuantity;
    if (signedQuantity === 0 || Math.sign(signedQuantity) === Math.sign(signedDelta)) {
      const nextAbsQuantity = Math.abs(signedQuantity) + tradeQuantity;
      averageEntry =
        nextAbsQuantity > 0
          ? (Math.abs(signedQuantity) * averageEntry + tradeQuantity * trade.price) / nextAbsQuantity
          : 0;
      signedQuantity += signedDelta;
      continue;
    }

    const closeQuantity = Math.min(Math.abs(signedQuantity), tradeQuantity);
    if (signedQuantity > 0 && trade.side === 'SELL') {
      realizedPnl += (trade.price - averageEntry) * closeQuantity;
    } else if (signedQuantity < 0 && trade.side === 'BUY') {
      realizedPnl += (averageEntry - trade.price) * closeQuantity;
    }

    const previousQuantity = signedQuantity;
    signedQuantity += signedDelta;

    if (signedQuantity === 0) {
      averageEntry = 0;
    } else if (Math.sign(previousQuantity) !== Math.sign(signedQuantity)) {
      averageEntry = trade.price;
    }
  }

  return roundPnl(realizedPnl);
}

export function buildPositionTradeColumns<TTrade extends PositionTradeLike>({
  positionTeamCode,
  trades,
  totalTradingPnl = null,
  realizedPnl = null,
  unrealizedPnl = null,
}: BuildPositionTradeColumnsArgs<TTrade>) {
  const normalizedTeamCode = positionTeamCode.trim().toUpperCase();
  const positionTrades = trades.filter(
    (trade) =>
      trade.team_code.trim().toUpperCase() === normalizedTeamCode &&
      trade.trade_type !== 'SETTLEMENT',
  );

  const buyTrades = positionTrades.filter((trade) => trade.side === 'BUY');
  const sellTrades = positionTrades.filter((trade) => trade.side === 'SELL');
  const materializedPnl = calculateMaterializedTradePnl(positionTrades);
  const displayRealizedPnl = realizedPnl == null ? materializedPnl : realizedPnl;
  const displayUnrealizedPnl =
    unrealizedPnl == null
      ? totalTradingPnl == null
        ? null
        : roundPnl(totalTradingPnl - materializedPnl)
      : unrealizedPnl;

  return {
    buyTrades,
    sellTrades,
    buySummary: summarizeTrades(buyTrades),
    sellSummary: summarizeTrades(sellTrades),
    pnlBreakdown: {
      realizedPnl: roundPnl(displayRealizedPnl),
      unrealizedPnl: displayUnrealizedPnl == null ? null : roundPnl(displayUnrealizedPnl),
    },
  };
}
